<script lang="ts" setup>
    import { computed, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import type { ChatAudioTranscript } from 'models'
    import { formatClock } from 'utils'

    const props = defineProps<{
        /** Where the recording plays from. */
        url: string
        /** What the turn heard, once it has (spec `audio-file-transcription.md`, D4). */
        transcript?: ChatAudioTranscript
        /** The turn is still transcribing it: the answer waits on it. */
        pending?: boolean
    }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    /** Arrives open: it is what the turn heard, and its height is bounded. */
    const isOpen = ref(true)

    const text = computed(() =>
        props.transcript?.status === 'ready'
            ? props.transcript.text?.trim()
            : undefined,
    )

    const duration = computed(() =>
        props.transcript?.durationSeconds
            ? formatClock(props.transcript.durationSeconds)
            : undefined,
    )

    /** Why there is no transcript to open, when there is none. */
    const missing = computed(() => {
        switch (props.transcript?.status) {
            case 'no-speech':
                return $t('messagePart.audioTranscriptNoSpeech')
            case 'failed':
                return $t('messagePart.audioTranscriptFailed')
            case 'off':
                return $t('messagePart.audioTranscriptOff')
            default:
                return undefined
        }
    })
</script>

<template>
    <div class="pk-chatbot-audio">
        <audio
            class="pk-chatbot-audio__player"
            :src="url"
            controls
            preload="none" />
        <!-- The same box as the steps of an answer (`PkChatbotSteps`): a
             header row that opens a body of bounded height. -->
        <div
            v-if="pending && !transcript"
            class="pk-chatbot-audio__box"
            role="status">
            <div class="pk-chatbot-audio__header">
                <VvIcon name="loading-loop" class="shrink-0" />
                <span class="pk-chatbot-audio__title">
                    {{ $t('messagePart.audioTranscribing') }}
                </span>
            </div>
        </div>
        <div v-else-if="text" class="pk-chatbot-audio__box">
            <button
                type="button"
                class="pk-chatbot-audio__header pk-chatbot-audio__header--toggle"
                :aria-expanded="isOpen"
                @click="isOpen = !isOpen">
                <VvIcon name="ri:file-text-line" class="shrink-0" />
                <span class="pk-chatbot-audio__title">
                    {{ $t('messagePart.audioTranscript') }}
                </span>
                <span v-if="duration" class="pk-chatbot-audio__meta">
                    {{ duration }}
                </span>
                <VvIcon
                    :name="
                        isOpen ? 'ri:arrow-up-s-line' : 'ri:arrow-down-s-line'
                    "
                    class="pk-chatbot-audio__chevron shrink-0 ml-auto" />
            </button>
            <div v-if="isOpen" class="pk-chatbot-audio__body light-scrollbar">
                {{ text }}
            </div>
        </div>
        <div v-else-if="missing" class="pk-chatbot-audio__box">
            <div class="pk-chatbot-audio__header">
                <VvIcon name="ri:file-text-line" class="shrink-0" />
                <span>{{ missing }}</span>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    // On the side of the message it belongs to, as wide as a transcript reads
    // well.
    .pk-chatbot-audio {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: var(--spacing-8);
        width: 100%;
        max-width: 28rem;
        min-width: 0;

        // As wide as the file card above.
        &__player {
            width: 100%;
            max-width: 280px;
        }

        // The frame and the rows of `.pk-chatbot-steps`, so a transcript reads
        // as one more thing the turn did.
        &__box {
            width: 100%;
            border: var(--spacing-px) solid var(--color-surface-3);
            border-radius: var(--rounded-lg);
            background-color: var(--color-surface-1);
            font-size: var(--text-12);
            color: var(--color-word-3);
            overflow: hidden;
        }

        &__header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: 100%;
            min-height: var(--spacing-40);
            padding: var(--spacing-8) var(--spacing-sm);
            text-align: left;

            &--toggle {
                cursor: pointer;
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: var(--color-surface-2);
                }
            }
        }

        &__title {
            font-weight: 600;
            color: var(--color-word-2);
            flex-grow: 1;
        }

        &__meta {
            color: var(--color-word-4);
            font-variant-numeric: tabular-nums;
        }

        &__chevron {
            font-size: var(--text-16);
        }

        // The paragraphs and their [hh:mm:ss] stamps are the transcript's
        // own; a long meeting scrolls inside, as a finished reasoning does.
        &__body {
            max-height: var(--spacing-256);
            overflow: auto;
            padding: var(--spacing-8) var(--spacing-sm);
            border-top: var(--spacing-px) solid var(--color-surface-3);
            background-color: var(--color-surface);
            color: var(--color-word-2);
            line-height: 1.6;
            white-space: pre-line;
            overflow-wrap: anywhere;
        }
    }
</style>
